import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useSEO, generateLocalBusinessSchema, generateServiceSchema } from '../../utils/seo';

export default function ListingHermanusPage() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');

  // SEO Configuration
  useSEO({
    title: 'Hermanus Professional Painters - Coastal Painting Specialists | Residential & Commercial',
    description: 'Expert coastal painting services in Hermanus, Sandbaai, and Onrus. Specialized in weather-resistant coatings for beachfront properties. Over 15 years of experience with guaranteed quality.',
    keywords: 'painters Hermanus, coastal painting Hermanus, house painters Hermanus, residential painting, commercial painting, beachfront painting',
    schema: {
      '@context': 'https://schema.org',
      '@graph': [
        generateLocalBusinessSchema({
          name: 'Painters Hermanus',
          description: 'Coastal painting specialists in Hermanus',
          address: 'Hermanus, Western Cape',
          priceRange: '$$',
          serviceArea: ['Hermanus', 'Sandbaai', 'Onrus', 'Vermont', 'Kleinmond']
        }),
        generateServiceSchema({
          name: 'Coastal Painting Services',
          description: 'Specialized coastal and residential painting',
          provider: 'Painters Hermanus',
          areaServed: ['Hermanus', 'Sandbaai', 'Onrus'],
          serviceType: ['Coastal Painting', 'Residential Painting', 'Commercial Painting']
        })
      ]
    }
  });

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const listing = {
    name: "Painters Hermanus",
    location: "Hermanus, Western Cape",
    website: "https://paintershermanus.co.za/",
    heroImage: "https://public.readdy.ai/ai/img_res/48fcc18617f2bfeb26ffe61c49ca7966.jpg",
    gallery: [
      "https://readdy.ai/api/search-image?query=beautifully%20painted%20coastal%20house%20in%20Hermanus%20with%20ocean%20views%20white%20walls%20blue%20trim%20professional%20finish%20mountain%20backdrop%20scenic%20setting&width=800&height=600&seq=gallery-hermanus1&orientation=landscape",
      "https://readdy.ai/api/search-image?query=interior%20painting%20project%20in%20Hermanus%20home%20elegant%20coastal%20living%20room%20with%20feature%20wall%20ocean%20inspired%20colors%20natural%20lighting%20professional%20finish&width=800&height=600&seq=gallery-hermanus2&orientation=landscape",
      "https://readdy.ai/api/search-image?query=exterior%20house%20painting%20in%20Hermanus%20coastal%20property%20with%20scaffolding%20professional%20painters%20working%20on%20facade%20ocean%20views%20mountain%20backdrop&width=800&height=600&seq=gallery-hermanus3&orientation=landscape",
      "https://readdy.ai/api/search-image?query=coastal%20property%20painting%20in%20Hermanus%20with%20marine%20grade%20protection%20weatherproof%20coating%20ocean%20front%20house%20professional%20application&width=800&height=600&seq=gallery-hermanus4&orientation=landscape"
    ],
    services: [
      {
        title: "Coastal Painting",
        description: "Specialized painting services for Hermanus coastal properties. We use marine-grade paints and protective coatings specifically designed to withstand salt air, ocean winds, and harsh coastal conditions.",
        features: ["Marine-grade paint systems", "Salt damage prevention", "Wind-resistant coatings", "Coastal weatherproofing", "Ocean-view property specialists", "Corrosion protection"]
      },
      {
        title: "Interior Painting",
        description: "Transform your Hermanus home's interior with our expert painting services. We create beautiful, durable finishes that complement the coastal lifestyle while protecting against humidity and moisture.",
        features: ["Living areas & bedrooms", "Kitchens & bathrooms", "Coastal color schemes", "Moisture-resistant paints", "Feature walls", "Ceiling painting"]
      },
      {
        title: "Exterior Painting",
        description: "Protect and enhance your property's exterior with our professional painting services. We understand the unique challenges of Hermanus weather and use premium products designed for coastal environments.",
        features: ["House facades", "Weatherboard protection", "Fascias & soffits", "Garden walls", "UV-resistant coatings", "Pressure washing"]
      }
    ],
    serviceAreas: ["Hermanus", "Voelklip", "Eastcliff", "Westcliff", "Sandbaai", "Onrus", "Vermont", "Hawston", "Stanford"],
    about: "Painters Hermanus is the premier coastal painting company serving Hermanus and the Overberg region for over 14 years. We specialize in protecting and beautifying properties in one of South Africa's most beautiful coastal towns, where the unique marine environment demands specialized painting expertise and premium materials.",
    aboutExtended: [
      "Our team of experienced painters understands the specific challenges posed by Hermanus's coastal climate. From salt-laden ocean breezes to intense UV exposure and variable weather patterns, we use only the highest quality marine-grade paints and protective coatings designed to withstand these harsh conditions while maintaining their beauty for years to come.",
      "What sets Painters Hermanus apart is our deep knowledge of coastal architecture and our commitment to preserving the character of this historic whale-watching town. Whether you own a charming heritage cottage in the old village, a modern ocean-view home in Voelklip, or a commercial property along the cliff path, we bring specialized expertise to every project.",
      "We take pride in our reputation for reliability, quality workmanship, and exceptional customer service. Our comprehensive approach includes detailed surface preparation, specialized primers for coastal conditions, premium paint application, and thorough protection of your property throughout the process. Every project is completed to the highest standards with minimal disruption to your daily life."
    ],
    ourApproach: [
      "Free coastal property assessment and quotation",
      "Specialized surface preparation for marine environments",
      "Marine-grade paints and protective coatings",
      "Expert application by coastal painting specialists",
      "Weather-dependent scheduling for optimal results",
      "Complete property protection during work",
      "Thorough cleanup and waste removal",
      "Quality inspection and warranty coverage"
    ],
    whyChoose: [
      "14+ years serving Hermanus",
      "Coastal painting specialists",
      "Marine-grade materials",
      "Fully licensed and insured",
      "Free color consultation",
      "Heritage property expertise",
      "Competitive pricing",
      "Weather-resistant guarantees",
      "Local knowledge and experience",
      "Eco-friendly options available"
    ],
    testimonials: [
      {
        name: "Jennifer Roberts",
        rating: 5,
        text: "Outstanding service! Our ocean-front home in Voelklip looks absolutely stunning. The team understood the coastal challenges and used the right products. Three years later and the paint still looks perfect despite the harsh conditions."
      },
      {
        name: "Peter Botha",
        rating: 5,
        text: "We've used Painters Hermanus for both our home and our guesthouse. Professional, reliable, and the quality is exceptional. They really know how to handle coastal properties. Highly recommended!"
      },
      {
        name: "Amanda Clarke",
        rating: 5,
        text: "Fantastic experience from start to finish! They were punctual, clean, and the attention to detail was impressive. Our heritage cottage looks beautiful and we're confident the marine-grade paint will last for years."
      }
    ]
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className={`sticky top-0 z-50 border-b border-gray-800 transition-all duration-300 ${isScrolled ? 'bg-black text-white' : 'bg-black text-white'}`}>
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="text-3xl font-bold whitespace-nowrap">
              <span className="text-white">PAINTE</span>
              <span className="text-[#e9f638]">RRR</span>
            </Link>
            <nav className="hidden md:flex items-center space-x-8">
              <Link to="/" className="hover:text-[#e9f638] transition whitespace-nowrap">Home</Link>
              <Link to="/about" className="hover:text-[#e9f638] transition whitespace-nowrap">About</Link>
              <Link to="/contact" className="bg-[#e9f638] text-black px-6 py-2 rounded-lg font-semibold hover:bg-white transition whitespace-nowrap cursor-pointer">Get a Quote</Link>
            </nav>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <div className="relative h-96 w-full overflow-hidden">
        <img 
          src={listing.heroImage}
          alt={listing.name}
          className="w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent"></div>
        <div className="absolute bottom-0 left-0 right-0 container mx-auto px-4 pb-8">
          <h1 className="text-5xl font-bold text-white mb-2">{listing.name}</h1>
          <p className="text-xl text-gray-200 flex items-center">
            <i className="ri-map-pin-line mr-2"></i>
            {listing.location}
          </p>
        </div>
      </div>

      {/* Contact Bar */}
      <div className="bg-[#e9f638] py-4">
        <div className="container mx-auto px-4">
          <div className="flex flex-col md:flex-row justify-center items-center gap-4">
            <a 
              href={listing.website}
              target="_blank"
              rel="noopener noreferrer"
              className="bg-black text-white px-8 py-3 rounded-lg font-bold hover:bg-gray-800 transition whitespace-nowrap cursor-pointer"
            >
              Visit Website
            </a>
          </div>
        </div>
      </div>

      {/* Tabs Navigation */}
      <div className="bg-gray-50 border-b border-gray-200 sticky top-[73px] z-40">
        <div className="container mx-auto px-4">
          <div className="flex space-x-8 overflow-x-auto">
            <button
              onClick={() => setActiveTab('overview')}
              className={`py-4 px-2 font-semibold whitespace-nowrap border-b-2 transition cursor-pointer ${
                activeTab === 'overview' ? 'border-[#e9f638] text-black' : 'border-transparent text-gray-500 hover:text-black'
              }`}
            >
              Overview
            </button>
            <button
              onClick={() => setActiveTab('services')}
              className={`py-4 px-2 font-semibold whitespace-nowrap border-b-2 transition cursor-pointer ${
                activeTab === 'services' ? 'border-[#e9f638] text-black' : 'border-transparent text-gray-500 hover:text-black'
              }`}
            >
              Services
            </button>
            <button
              onClick={() => setActiveTab('gallery')}
              className={`py-4 px-2 font-semibold whitespace-nowrap border-b-2 transition cursor-pointer ${
                activeTab === 'gallery' ? 'border-[#e9f638] text-black' : 'border-transparent text-gray-500 hover:text-black'
              }`}
            >
              Gallery
            </button>
            <button
              onClick={() => setActiveTab('reviews')}
              className={`py-4 px-2 font-semibold whitespace-nowrap border-b-2 transition cursor-pointer ${
                activeTab === 'reviews' ? 'border-[#e9f638] text-black' : 'border-transparent text-gray-500 hover:text-black'
              }`}
            >
              Reviews
            </button>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="container mx-auto px-4 py-12">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2">
            {activeTab === 'overview' && (
              <div>
                <section className="mb-12">
                  <h2 className="text-3xl font-bold mb-6">About Painters Hermanus</h2>
                  <p className="text-gray-700 text-lg leading-relaxed mb-4">{listing.about}</p>
                  {listing.aboutExtended.map((paragraph, index) => (
                    <p key={index} className="text-gray-700 text-lg leading-relaxed mb-4">{paragraph}</p>
                  ))}
                </section>

                <section className="mb-12">
                  <h3 className="text-2xl font-bold mb-6">Our Professional Approach</h3>
                  <div className="bg-gray-50 p-6 rounded-lg border-2 border-gray-200">
                    <ul className="space-y-3">
                      {listing.ourApproach.map((step, index) => (
                        <li key={index} className="flex items-start">
                          <i className="ri-checkbox-circle-line text-[#e9f638] text-2xl mr-3 mt-1"></i>
                          <span className="text-gray-700 text-lg">{step}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </section>

                <section className="mb-12">
                  <h3 className="text-2xl font-bold mb-6">Why Choose Painters Hermanus</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {listing.whyChoose.map((reason, index) => (
                      <div key={index} className="flex items-start">
                        <i className="ri-check-line text-[#e9f638] text-2xl mr-3 mt-1"></i>
                        <span className="text-gray-700 text-lg">{reason}</span>
                      </div>
                    ))}
                  </div>
                </section>

                <section>
                  <h3 className="text-2xl font-bold mb-6">Areas We Serve</h3>
                  <p className="text-gray-700 text-lg mb-4">We proudly serve the following areas in the Hermanus and Overberg region:</p>
                  <div className="flex flex-wrap gap-3">
                    {listing.serviceAreas.map((area, index) => (
                      <span key={index} className="bg-gray-100 text-black px-4 py-2 rounded-full font-semibold">
                        {area}
                      </span>
                    ))}
                  </div>
                </section>
              </div>
            )}

            {activeTab === 'services' && (
              <div>
                <h2 className="text-3xl font-bold mb-4">Our Professional Painting Services</h2>
                <p className="text-gray-700 text-lg mb-8">We offer specialized painting services designed for Hermanus coastal properties and the unique challenges of the marine environment.</p>
                <div className="space-y-8">
                  {listing.services.map((service, index) => (
                    <div key={index} className="bg-gray-50 p-6 rounded-lg border-2 border-gray-200 hover:border-[#e9f638] transition">
                      <h3 className="text-2xl font-bold mb-3">{service.title}</h3>
                      <p className="text-gray-700 mb-4 text-lg">{service.description}</p>
                      <h4 className="font-semibold text-lg mb-3">What We Offer:</h4>
                      <ul className="space-y-2">
                        {service.features.map((feature, idx) => (
                          <li key={idx} className="flex items-center text-gray-700">
                            <i className="ri-arrow-right-s-line text-[#e9f638] mr-2"></i>
                            {feature}
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {activeTab === 'gallery' && (
              <div>
                <h2 className="text-3xl font-bold mb-8">Project Gallery</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {listing.gallery.map((image, index) => (
                    <div key={index} className="h-80 w-full overflow-hidden rounded-lg shadow-lg hover:shadow-2xl transition">
                      <img 
                        src={image}
                        alt={`Project ${index + 1}`}
                        className="w-full h-full object-cover object-top hover:scale-110 transition-transform duration-500 cursor-pointer"
                      />
                    </div>
                  ))}
                </div>
              </div>
            )}

            {activeTab === 'reviews' && (
              <div>
                <h2 className="text-3xl font-bold mb-8">Customer Reviews</h2>
                <div className="space-y-6">
                  {listing.testimonials.map((testimonial, index) => (
                    <div key={index} className="bg-gray-50 p-6 rounded-lg border-2 border-gray-200">
                      <div className="flex items-center mb-4">
                        <div className="flex mr-4">
                          {[...Array(testimonial.rating)].map((_, i) => (
                            <i key={i} className="ri-star-fill text-[#e9f638] text-xl"></i>
                          ))}
                        </div>
                        <span className="font-bold text-lg">{testimonial.name}</span>
                      </div>
                      <p className="text-gray-700 text-lg leading-relaxed">{testimonial.text}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Sidebar */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-lg shadow-lg p-6 sticky top-24">
              <h3 className="text-2xl font-bold mb-6">Contact Information</h3>
              
              <div className="space-y-4 mb-6">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 flex items-center justify-center">
                    <i className="ri-map-pin-line text-2xl text-[#e9f638]"></i>
                  </div>
                  <div>
                    <p className="font-semibold">Service Areas</p>
                    <p className="text-gray-600">Hermanus, Voelklip, Sandbaai</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 flex items-center justify-center">
                    <i className="ri-time-line text-2xl text-[#e9f638]"></i>
                  </div>
                  <div>
                    <p className="font-semibold">Business Hours</p>
                    <p className="text-gray-600">Mon-Fri: 7:30AM-5PM</p>
                    <p className="text-gray-600">Sat: 8AM-12PM</p>
                  </div>
                </div>
              </div>
              
              <a 
                href={listing.website}
                target="_blank"
                rel="noopener noreferrer"
                className="block w-full bg-[#e9f638] text-black text-center py-3 rounded-lg font-bold hover:bg-black hover:text-[#e9f638] transition-colors duration-300 whitespace-nowrap cursor-pointer"
              >
                Visit Website
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-black text-white border-t border-gray-800">
        <div className="container mx-auto px-4 py-12">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8 mb-8">
            <div>
              <h3 className="text-2xl font-bold mb-4">
                <span className="text-white">PAINTE</span>
                <span className="text-[#e9f638]">RRR</span>
              </h3>
              <p className="text-gray-400">South Africa's premier directory for professional house painters.</p>
            </div>
            <div>
              <h4 className="font-semibold mb-4 text-[#e9f638]">Quick Links</h4>
              <ul className="space-y-2">
                <li><Link to="/" className="text-gray-400 hover:text-[#e9f638] cursor-pointer">Home</Link></li>
                <li><Link to="/about" className="text-gray-400 hover:text-[#e9f638] cursor-pointer">About</Link></li>
                <li><Link to="/contact" className="text-gray-400 hover:text-[#e9f638] cursor-pointer">Contact</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-4 text-[#e9f638]">Service Areas</h4>
              <ul className="space-y-2 text-gray-400">
                <li>Hermanus</li>
                <li>Voelklip</li>
                <li>Sandbaai</li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-4 text-[#e9f638]">Connect</h4>
              <p className="text-gray-400">Find the perfect painter for your project today.</p>
            </div>
          </div>
          <div className="border-t border-gray-800 pt-8 flex flex-col md:flex-row justify-between items-center text-sm text-gray-400">
            <p>© 2025 PAINTERRR. All rights reserved.</p>
            <div className="flex space-x-4 mt-4 md:mt-0">
              <a href="https://readdy.ai/?origin=logo" target="_blank" rel="noopener noreferrer" className="hover:text-[#e9f638] cursor-pointer">
                Powered by Readdy
              </a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
